import { useMemo } from 'react';
import { useIntl, IntlShape } from 'react-intl';
import type { Content } from '@plone/types';
import type { CardProps } from '@designsystem/stories/03-molecules/Card';
import type { OccasionProps } from '@fhnw/types/occasion';
import {
  occasionVisible,
  transformToCard,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import type { RegisterContent } from '@fhnw/helpers/DesignSystem/types';
import { useOccasionRegistration } from '@fhnw/components/OccasionRegistrationPanel';
import { APPLICATION_CONFIG } from '../config';

interface ProcessedOccasions {
  occasions: CardProps[];
  hasMore: boolean;
}

/**
 * Sorts occasions based on rolling_intake and occasion_start
 * Applying SOLID principle: Single Responsibility
 */
const sortOccasions = (occasions: OccasionProps[]): OccasionProps[] => {
  return [...occasions].sort((a, b) => {
    // Rolling intake has priority
    if (a.rolling_intake && !b.rolling_intake) return -1;
    if (!a.rolling_intake && b.rolling_intake) return 1;

    // Sort by start date
    if (a.occasion_start && b.occasion_start) {
      return (
        new Date(a.occasion_start).getTime() -
        new Date(b.occasion_start).getTime()
      );
    }
    return 0;
  });
};

/**
 * Processes and normalizes occasions for display
 * Applying DRY principle: Reusable logic
 */
const processOccasions = (
  items: OccasionProps[],
  maxItems: number,
  content: Content,
  intl: IntlShape,
  onAnmelden?: (item: RegisterContent) => void,
): ProcessedOccasions => {
  const occasions: CardProps[] = [];
  let visibleCount = 0;

  for (const occasion of items) {
    if (!occasionVisible(occasion, content)) {
      continue;
    }

    visibleCount++;

    if (occasions.length >= maxItems) {
      continue;
    }

    occasions.push(
      transformToCard(
        {
          ...occasion,
          '@id': content['@id'] + '/' + occasion['@id'],
          '@type': APPLICATION_CONFIG.DEFAULTS.OCCASION_TYPE,
          parent: content,
        },
        intl,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        onAnmelden,
      ),
    );
  }

  return {
    occasions,
    hasMore: visibleCount > maxItems,
  };
};

interface UseOccasionsProps {
  content: Content;
  maxItems: number;
  sortByFolderPosition?: boolean;
}

/**
 * Custom Hook for occasion management
 * Encapsulates sorting and processing of occasions
 */
export const useOccasions = ({
  content,
  maxItems,
  sortByFolderPosition = APPLICATION_CONFIG.DEFAULTS.SORT_BY_FOLDER_POSITION,
}: UseOccasionsProps): ProcessedOccasions => {
  const intl = useIntl();
  const { openForOccasion } = useOccasionRegistration();

  // Memoized sorting - performance optimization
  const sortedItems = useMemo(() => {
    const items: OccasionProps[] = [...(content?.occasions || [])];

    if (!sortByFolderPosition) {
      return sortOccasions(items);
    }

    return items;
  }, [content?.occasions, sortByFolderPosition]);

  // Memoized occasion processing - performance optimization
  const processedOccasions = useMemo(() => {
    const onAnmelden = (occ: RegisterContent) =>
      openForOccasion({
        uid: occ.UID as string | undefined,
        event: (occ.parent ?? content) as any,
        occasion: occ as any,
      });
    return processOccasions(sortedItems, maxItems, content, intl, onAnmelden);
  }, [sortedItems, maxItems, content, intl, openForOccasion]);

  return processedOccasions;
};
